import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, UserCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// UserMenu
// ---------------------------------------------------------------------------
// The right-most item in TopNavBar: user identity + a click-to-open dropdown
// for account actions (logout, profile, etc). Built as a separate component
// from NavDropdown rather than a third "variant" of it because its trigger
// is meaningfully different (an identity chip with a name/role and avatar,
// not a plain labeled button) even though the dropdown-panel mechanics
// (click-outside, Escape-to-close) are conceptually the same.
//
// Usage (Phase 2 supplies real user data and a logout handler):
//
//   <UserMenu
//     name={user.name}
//     role={user.role}
//     items={[
//       { label: 'Log out', icon: LogOut, onClick: handleLogout },
//     ]}
//   />
//
// Props:
//   - name (string, required): the signed-in user's display name.
//   - role (string, optional): shown as a smaller line under the name
//     (e.g. "Administrator", "Front Desk Agent").
//   - items (array, required): dropdown entries, same shape as
//     NavDropdown's `items`: { label: string, icon?: component, onClick?: () => void }
//   - icon (component, optional): override the default lucide UserCircle
//     avatar glyph shown in the trigger.
function UserMenu({ name, role, items = [], icon: Icon = UserCircle }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 rounded text-shell-text hover:bg-shell-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-shell transition-colors"
      >
        <Icon className="w-6 h-6" aria-hidden="true" />
        <span className="text-left leading-tight hidden sm:block">
          <span className="block text-sm font-medium">{name}</span>
          {role && <span className="block text-xs text-shell-light/80">{role}</span>}
        </span>
        <ChevronDown className="w-4 h-4" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 min-w-[12rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.label ?? index}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick && item.onClick();
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-shell-light hover:text-shell-dark focus:outline-none focus-visible:bg-shell-light"
              >
                {ItemIcon && <ItemIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UserMenu;
